/* =====================================================================================
   Scribe: capture a meeting, have it processed, act on what came out.

   Live-only. Capture writes the meeting row (and the recording into the scribe bucket),
   scribe-process does the thinking on the edge, and everything the model proposes comes back
   through row-level security as rows a person can read, edit, complete or approve.
   ===================================================================================== */

import { BACKEND } from './db';
import { explain } from './dberrors';

const live = () => BACKEND === 'supabase';

export interface Meeting {
  id: string; reference: string | null; title: string; held_at: string;
  medium: string | null; location: string | null;
  attendees: { name: string; email?: string; organisation?: string; share?: boolean }[];
  account_id: string | null; opportunity_id: string | null; site_id: string | null;
  audio_path: string | null; transcript: string | null; transcript_source: string | null;
  status: string; error: string | null; summary: string | null;
  decisions: string[]; minutes: string | null; interaction_id: string | null;
  created_at: string;
}

export interface Action {
  id: string; meeting_id: string; title: string; detail: string | null;
  owner_name: string | null; owner_member_id: string | null; due_on: string | null;
  source_quote: string | null; status: string;
}

export interface Draft {
  id: string; to_email: string; to_name: string | null; subject: string; body: string;
  status: string; object_id: string | null;
}

export async function fetchMeetings(): Promise<Meeting[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('scribe').from('meetings')
    .select('*').order('held_at', { ascending: false });
  if (error) throw new Error(explain(error.message, 'read the meetings'));
  return (data ?? []) as Meeting[];
}

export async function createMeeting(m: {
  title: string; heldAt: string; medium?: string; location?: string;
  attendees: { name: string; email?: string; organisation?: string; share?: boolean }[];
  accountId?: string | null; transcript?: string; transcriptSource?: 'typed' | 'pasted';
}): Promise<Meeting> {
  const { supabase } = await import('./supabase');
  const sb = supabase();
  const { data: ref } = await sb.schema('scribe').rpc('new_reference');
  const { data: me } = await sb.schema('app').rpc('current_member_id');
  const { data, error } = await sb.schema('scribe').from('meetings').insert({
    reference: ref ?? null, title: m.title, held_at: m.heldAt,
    medium: m.medium || null, location: m.location || null,
    attendees: m.attendees.filter((a) => a.name.trim()),
    account_id: m.accountId || null,
    transcript: m.transcript?.trim() || null,
    transcript_source: m.transcript?.trim() ? (m.transcriptSource ?? 'pasted') : null,
    created_by: me ?? null,
  }).select('*').single();
  if (error) throw new Error(explain(error.message, 'save the meeting'));
  return data as Meeting;
}

/** Uploads a recording or audio file and pins it to the meeting. */
export async function attachAudio(meetingId: string, blob: Blob, mime: string) {
  const { supabase } = await import('./supabase');
  const sb = supabase();
  const { data: org } = await sb.schema('app').rpc('current_org_id');
  const ext = mime.includes('webm') ? 'webm' : mime.includes('mp4') || mime.includes('m4a') ? 'm4a'
    : mime.includes('wav') ? 'wav' : mime.includes('ogg') ? 'ogg' : 'mp3';
  const path = `${org}/${meetingId}.${ext}`;
  const up = await sb.storage.from('scribe').upload(path, blob, { contentType: mime, upsert: true });
  if (up.error) throw new Error(explain(up.error.message, 'store the recording'));
  const { error } = await sb.schema('scribe').from('meetings')
    .update({ audio_path: path, audio_mime: mime }).eq('id', meetingId);
  if (error) throw new Error(explain(error.message, 'record the upload'));
}

export async function processMeeting(meetingId: string): Promise<{
  ok?: boolean; actions?: number; decisions?: number; drafts?: number; error?: string;
}> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().functions.invoke('scribe-process',
    { body: { meeting_id: meetingId } });
  if (error) {
    /* A non-2xx from the function arrives as FunctionsHttpError with the useful message in
       the response body, which supabase-js does not surface. Dig it out. */
    const ctx = (error as { context?: Response }).context;
    if (ctx) {
      try { const j = await ctx.json(); if (j?.error) return { error: String(j.error) }; }
      catch { /* fall through */ }
    }
    return { error: error.message };
  }
  return data ?? {};
}

export async function fetchActions(): Promise<Action[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('scribe').from('actions')
    .select('*').order('due_on', { ascending: true, nullsFirst: false });
  if (error) throw new Error(explain(error.message, 'read the actions'));
  return (data ?? []) as Action[];
}

export const setActionStatus = async (id: string, status: 'open' | 'done' | 'dropped') => {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('scribe').from('actions')
    .update({ status, done_at: status === 'done' ? new Date().toISOString() : null }).eq('id', id);
  if (error) throw new Error(explain(error.message, 'update the action'));
};

export async function fetchDrafts(meetingId?: string): Promise<Draft[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  let q = supabase().schema('app').from('mail_outbox')
    .select('id, to_email, to_name, subject, body, status, object_id')
    .eq('object_type', 'scribe_meeting').order('created_at', { ascending: false });
  if (meetingId) q = q.eq('object_id', meetingId);
  const { data, error } = await q;
  if (error) throw new Error(explain(error.message, 'read the drafts'));
  return (data ?? []) as Draft[];
}

export const updateDraft = async (id: string, patch: Partial<Draft>) => {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').from('mail_outbox').update(patch).eq('id', id);
  if (error) throw new Error(explain(error.message, 'update the draft'));
};

export const approveDraft = async (id: string) => {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').rpc('approve_mail', { p_id: id });
  if (error) throw new Error(explain(error.message, 'approve the draft'));
};

export async function fetchAccountOptions(): Promise<{ id: string; name: string }[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('crm').from('accounts')
    .select('id, name').order('name').limit(200);
  return (data ?? []) as { id: string; name: string }[];
}

/** Sends an approved (or failed, retried) draft through the tenant's Graph mailbox. */
export async function sendDraft(id: string): Promise<{ ok?: boolean; sent_as?: string; error?: string }> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().functions.invoke('send-mail', { body: { mail_id: id } });
  if (error) {
    const ctx = (error as { context?: Response }).context;
    if (ctx) { try { const j = await ctx.json(); if (j?.error) return { error: String(j.error) }; } catch { /* fall through */ } }
    return { error: error.message };
  }
  return data ?? {};
}
